import i18n from 'i18next';
import { notify } from '@koob/ui';
import { TravelDiaryPriceDisplayLevelEnum } from '@koob/enums';
import { getPricingSummary, savePricingSummary } from 'api/pricingSummaries';
import { getCurrencies } from 'api/currencies';
import { formatCurrencyLabel } from 'utils';
import { omit, round } from 'lodash';
import { getPricingCategory } from '@koob/utils';

/**
 * Pricing Summary store slice.
 *
 * Single source of truth for quote and receipt management (Trip or Booking).
 */

const calculatePriceWithoutMarkup = (price, markup, isMarkupPercent) => {
  const numericPrice = Number(price ?? 0);
  const numericMarkup = Number(markup ?? 0);

  if (numericMarkup === 0) return numericPrice;

  if (isMarkupPercent) {
    return numericPrice / (1 + numericMarkup / 100);
  }

  return numericPrice - numericMarkup;
};

export const calculateConvertedPrice = (
  price,
  currency,
  rate,
  targetCurrency,
) => {
  if (!targetCurrency) {
    return null;
  }
  if (currency && currency === targetCurrency) {
    return Number(price ?? 0);
  }
  if (rate == null || rate === '') {
    return null;
  }
  const numericRate = Number(rate);
  if (isNaN(numericRate) || numericRate <= 0) {
    return null;
  }
  return Math.round(Number(price ?? 0) * numericRate);
};

const getServiceConvertedPrice = (service, rate, targetCurrency) => {
  const isCancelled = ['cancelled', 'canceled'].includes(
    service?.state?.toLowerCase(),
  );
  if (isCancelled) {
    if (service?.cancellationFee == null) return null;
    return calculateConvertedPrice(
      service.cancellationFee,
      service?.currency,
      rate,
      targetCurrency,
    );
  }
  return calculateConvertedPrice(
    service?.price ?? 0,
    service?.currency,
    rate,
    targetCurrency,
  );
};

export const createPricingSummaryModel = (set, get) => ({
  // Initial State
  pricingSummary: null,
  // Snapshot taken on load/save, used to detect unsaved changes.
  isPricingSummaryLoading: false,
  isPricingSummarySaving: false,
  currencies: [],
  isLoadingCurrencies: false,
  targetCurrency: null,
  exchangeRate: null,

  /**
   *  Initializes or resets the Pricing Summary data.
   */
  initPricingSummary: async (resourceType, resourceId, fallbackItems = []) => {
    set({ isPricingSummaryLoading: true }, false, 'pricingSummary/initStart');

    const defaultNotes = [
      {
        id: crypto.randomUUID(),
        title: 'Notes',
        isGeneric: true,
        notes: '',
      },
    ];

    try {
      const response = await getPricingSummary({ resourceType, resourceId });
      const data = response?.data;

      const targetCurrency = data?.targetCurrency ?? null;
      const exchangeRate = data?.exchangeRate ?? null;

      const defaultServices = fallbackItems.map(item => {
        const price = Number(item.price || 0);
        return {
          ...item,
          markup: 0,
          category: getPricingCategory(item),
          isGeneric: true,
          isMarkupPercent: false,
          priceWithoutMarkup: price,
          convertedPrice: getServiceConvertedPrice(
            { ...item, price },
            exchangeRate ?? get().exchangeRate,
            targetCurrency ?? get().targetCurrency,
          ),
        };
      });

      const services = data?.services?.map(service => {
        const markup = service.markup ?? service.margin ?? 0;
        const isMarkupPercent =
          service.isMarkupPercent ?? service.isMarginPercent ?? false;
        const price = Number(service.totalPrice ?? service.price ?? 0);

        const updatedService = {
          ...service,
          type: service.kind,
          category: getPricingCategory({ ...service, type: service.kind }),
          markup,
          isMarkupPercent,
          price,
          priceWithoutMarkup: calculatePriceWithoutMarkup(
            price,
            markup,
            isMarkupPercent,
          ),
        };

        return {
          ...updatedService,
          convertedPrice: getServiceConvertedPrice(
            updatedService,
            exchangeRate ?? get().exchangeRate,
            targetCurrency ?? get().targetCurrency,
          ),
        };
      });

      const pricingSummaryData = {
        resourceType,
        resourceId,
        currency: data?.currency ?? fallbackItems[0]?.currency,
        markup: data?.margin ?? 0,
        isMarkupPercent: data?.isMarginPercent ?? false,
        displayMode: data?.displayMode ?? data?.pricingMode ?? 'itemized',
        services: services?.length ? services : defaultServices,
        additionalInformations: data?.additionalInformations?.length
          ? data.additionalInformations
          : defaultNotes,
        isTravelDiaryPriceDisplayed: data?.isTravelDiaryPriceDisplayed ?? false,
        travelDiaryPriceDisplayLevel:
          data?.travelDiaryPriceDisplayLevel ??
          (data?.isTravelDiaryPriceDisplayed
            ? TravelDiaryPriceDisplayLevelEnum.TOTAL
            : null),
        targetCurrency,
        exchangeRate,
      };

      set(
        {
          targetCurrency,
          exchangeRate,
          pricingSummary: pricingSummaryData,
          initialPricingSummary: pricingSummaryData,
          isPricingSummaryLoading: false,
        },
        false,
        'pricingSummary/initSuccess',
      );

      return pricingSummaryData;
    } catch (error) {
      const defaultServices = fallbackItems.map(item => {
        const price = Number(item.price || 0);
        return {
          ...item,
          markup: 0,
          category: getPricingCategory(item),
          isGeneric: true,
          isMarkupPercent: false,
          priceWithoutMarkup: price,
          convertedPrice: getServiceConvertedPrice(
            { ...item, price },
            get().exchangeRate,
            get().targetCurrency,
          ),
        };
      });

      const fallbackData = {
        resourceType,
        resourceId,
        currency: fallbackItems[0]?.currency,
        markup: 0,
        isMarkupPercent: false,
        displayMode: 'itemized',
        services: defaultServices,
        additionalInformations: defaultNotes,
        isTravelDiaryPriceDisplayed: false,
        travelDiaryPriceDisplayLevel: null,
        targetCurrency: null,
        exchangeRate: null,
      };

      set(
        {
          targetCurrency: null,
          exchangeRate: null,
          pricingSummary: fallbackData,
          initialPricingSummary: fallbackData,
          isPricingSummaryLoading: false,
        },
        false,
        'pricingSummary/initFallback',
      );

      return fallbackData;
    }
  },
  setPricingDisplayMode: displayMode => {
    const summary = get().pricingSummary;
    if (!summary) return;

    set(
      { pricingSummary: { ...summary, displayMode } },
      false,
      'pricingSummary/setDisplayMode',
    );
  },
  /**
   * Apply the overall margin to all services
   */
  setGlobalMarkup: markupData => {
    const summary = get().pricingSummary;
    if (!summary) return;

    const value = markupData?.value ?? markupData?.markup ?? 0;
    const isPercent =
      markupData?.isPercent ?? markupData?.isMarkupPercent ?? true;

    const numericMarkup = Number(value);

    const updatedServices = (summary.services || []).map(service => {
      const basePrice = Number(service.priceWithoutMarkup || 0);

      const rawPrice = isPercent
        ? basePrice * (1 + numericMarkup / 100)
        : basePrice + numericMarkup;

      const price = round(rawPrice, 2);

      const updatedService = {
        ...service,
        markup: numericMarkup,
        isMarkupPercent: isPercent,
        price,
      };

      return {
        ...updatedService,
        convertedPrice: getServiceConvertedPrice(
          updatedService,
          get().exchangeRate,
          get().targetCurrency,
        ),
      };
    });

    set(
      {
        pricingSummary: {
          ...summary,
          markup: numericMarkup,
          isMarkupPercent: isPercent,
          services: updatedServices,
        },
      },
      false,
      'pricingSummary/setGlobalMarkup',
    );
  },

  /**
   * Setter for the list of services (compatible with an update value or function)
   */
  setPricingServices: action => {
    const currentSummary = get().pricingSummary;
    if (!currentSummary) return;

    const nextServices =
      typeof action === 'function' ? action(currentSummary.services) : action;

    const updatedServices = (nextServices || []).map(service => ({
      ...service,
      convertedPrice: getServiceConvertedPrice(
        service,
        get().exchangeRate,
        get().targetCurrency,
      ),
    }));

    set(
      {
        pricingSummary: {
          ...currentSummary,
          services: updatedServices,
        },
      },
      false,
      'pricingSummary/setServices',
    );
  },

  /**
   * Setter for additional notes (compatible with update value or function)
   */
  setPricingAdditionalInformations: action => {
    const currentSummary = get().pricingSummary;
    if (!currentSummary) return;

    const nextInfos =
      typeof action === 'function'
        ? action(currentSummary.additionalInformations)
        : action;

    set(
      {
        pricingSummary: {
          ...currentSummary,
          additionalInformations: nextInfos,
        },
      },
      false,
      'pricingSummary/setAdditionalInformations',
    );
  },

  /**
   * Setter for the Travel Diary price display config (merges partial updates)
   */
  setTravelDiaryPriceDisplay: changes => {
    const currentSummary = get().pricingSummary;
    if (!currentSummary) return;

    set(
      {
        pricingSummary: {
          ...currentSummary,
          ...changes,
        },
      },
      false,
      'pricingSummary/setTravelDiaryPriceDisplay',
    );
  },

  /**
   * Saves the Pricing Summary to the API with a global notification.
   */
  savePricingSummary: async onSuccess => {
    const summary = get().pricingSummary;
    if (!summary) return;

    set({ isPricingSummarySaving: true }, false, 'pricingSummary/saveStart');

    const formattedAdditionalInformations = (
      summary.additionalInformations || []
    ).reduce((acc, info) => {
      if (!info.notes || info.notes.trim() === '') {
        return acc;
      }

      const { id, isGeneric, ...rest } = info;
      const cleanedInfo = {
        ...rest,
        ...(isGeneric ? {} : { id }),
      };

      acc.push(cleanedInfo);
      return acc;
    }, []);

    const formattedServices = (summary.services || []).map(service => {
      const fieldsToOmit = [
        'type',
        'markup',
        'isMarkupPercent',
        'price',
        'convertedPrice',
        ...(service.isGeneric ? ['id', 'isGeneric'] : []),
      ];

      return {
        ...omit(service, fieldsToOmit),
        ...(service.isGeneric ? {} : { id: service.id }),
        margin: service.markup,
        isMarginPercent: service.isMarkupPercent,
        totalPrice: service.price ?? service.priceWithoutMarkup,
        kind: service.type?.toLowerCase(),
      };
    });

    const payload = {
      ...omit(summary, ['markup', 'isMarkupPercent']),
      exchangeRate: summary.exchangeRate || null,
      margin: summary.markup ?? 0,
      isMarginPercent: summary.isMarkupPercent ?? false,
      services: formattedServices,
      additionalInformations: formattedAdditionalInformations,
      displayMode: summary.displayMode ?? 'itemized',
    };

    try {
      await savePricingSummary(payload);
      set(
        { isPricingSummarySaving: false, initialPricingSummary: summary },
        false,
        'pricingSummary/saveSuccess',
      );

      notify(
        i18n.t('trips:receipt.receipt:savedSuccess', 'Saved successfully'),
        {
          type: 'success',
        },
      );

      if (onSuccess) onSuccess();
    } catch (error) {
      set({ isPricingSummarySaving: false }, false, 'pricingSummary/saveError');

      notify(
        i18n.t('trips:receipt.savedError', 'An error occurred while saving'),
        {
          type: 'error',
        },
      );
    }
  },

  updatePricingSummary: (key, value) => {
    const state = get();
    const updatedKey = { [key]: value };
    const targetCurrency =
      key === 'targetCurrency' ? value : state.targetCurrency;
    const exchangeRate = key === 'exchangeRate' ? value : state.exchangeRate;

    const currentSummary = state.pricingSummary;
    if (currentSummary) {
      const updatedServices = (currentSummary.services || []).map(service => ({
        ...service,
        convertedPrice: getServiceConvertedPrice(
          service,
          exchangeRate,
          targetCurrency,
        ),
      }));

      set(
        {
          ...updatedKey,
          pricingSummary: {
            ...currentSummary,
            [key]: value,
            services: updatedServices,
          },
        },
        false,
        'pricingSummary/updatePricingSummaryWithConversion',
      );
    } else {
      set(updatedKey, false, 'pricingSummary/updatePricingSummary');
    }
  },

  fetchCurrencies: async () => {
    set(
      { isLoadingCurrencies: true },
      false,
      'pricingSummary/fetchCurrenciesStart',
    );
    try {
      const response = await getCurrencies();
      const listCurrency = response?.data || [];
      const currenciesOptions = listCurrency
        .map(({ name: currency }) => ({
          value: currency,
          label: formatCurrencyLabel(currency),
        }))
        .sort((currency1, currency2) =>
          currency1.label.localeCompare(currency2.label),
        );
      set(
        { currencies: currenciesOptions, isLoadingCurrencies: false },
        false,
        'pricingSummary/fetchCurrenciesSuccess',
      );
      return currenciesOptions;
    } catch (error) {
      set(
        { isLoadingCurrencies: false },
        false,
        'pricingSummary/fetchCurrenciesError',
      );
      return [];
    }
  },
});
